import { COLOR_CODES } from "../common";
import { lightenColor } from "../components/recharts/ReMixCharts";
import { ExchangeColors } from "../pages/Dashboard/Exchange3/FormatData";
import { getColorByKey } from "../utility/Colors";

/**
 * Generates a random but consistent color for a given string by using a more
 * sophisticated hashing algorithm combined with a large color palette
 * @param str - The input string to generate a color for
 * @param seedOffset - Optional offset to generate different colors for the same string
 * @returns A hex color code
 */

export const colorList = [
  "#64B5F6", // Darker Blue
  "#FFB74D", // Darker Orange
  "#81C784", // Darker Green
  "#E57373", // Darker Red
  "#9575CD", // Darker Purple
  "#616161", // Darker Gray
  COLOR_CODES.secondary,
  COLOR_CODES.tertiary,
  lightenColor(COLOR_CODES.hpx, 60),
  lightenColor(COLOR_CODES.pixl, 20),
  COLOR_CODES.primary,
  lightenColor(COLOR_CODES.dac, 10),
  lightenColor(COLOR_CODES.hpDaily, 20),
  COLOR_CODES.hpIntraDay,
  COLOR_CODES.hpDac,
  lightenColor(COLOR_CODES.secondary, 30),
  COLOR_CODES.daily,
  lightenColor(COLOR_CODES.iex, 50),
  COLOR_CODES.largeHydro,
  COLOR_CODES.hydro,
  lightenColor(COLOR_CODES.primary, 10),
  lightenColor(COLOR_CODES.secondary, 10),
  COLOR_CODES.weightedMCP,
  lightenColor(COLOR_CODES.dac, 20),
];

/**
 * Get a color directly by index
 */
export function getRandomColor(index: number): string {
  return colorList[index % colorList.length];
}

/**
 * Map of already used colors for consistent assignment
 */
const usedColorsMap = new Map<string, string>();
const COLOR_STORAGE_KEY = "insights.legend-color-map.v1";
let hasLoadedStoredColors = false;

function loadStoredColorMap() {
  if (hasLoadedStoredColors || typeof window === "undefined") {
    return;
  }

  hasLoadedStoredColors = true;
  try {
    const raw = window.localStorage.getItem(COLOR_STORAGE_KEY);
    if (!raw) {
      return;
    }

    const parsed = JSON.parse(raw) as Record<string, string>;
    Object.entries(parsed).forEach(([key, value]) => {
      if (typeof value === "string" && value.length > 0) {
        usedColorsMap.set(key, value);
      }
    });
  } catch {
    // Ignore storage parsing failures and continue with in-memory map.
  }
}

function saveStoredColorMap() {
  if (typeof window === "undefined") {
    return;
  }

  try {
    const entries = Object.fromEntries(usedColorsMap.entries());
    window.localStorage.setItem(COLOR_STORAGE_KEY, JSON.stringify(entries));
  } catch {
    // Ignore storage failures (private mode, quota, etc.).
  }
}

export function stringToConsistentColor(
  str: string,
  seedOffset: number = 0,
): string {
  const normalizedInput = str?.trim();
  if (!normalizedInput) {
    return "#888888";
  }

  loadStoredColorMap();

  const cacheKey = `${normalizedInput.toLowerCase()}|${seedOffset}`;
  const cachedColor = usedColorsMap.get(cacheKey);
  if (cachedColor) {
    return cachedColor;
  }

  const specialCases: Record<string, string> = {
    "interstate generation": "#64B5F6",
    "interstate generation price": "#FFB74D",
  };

  const selectedColor =
    specialCases[normalizedInput.toLowerCase()] ??
    getColorByKey(normalizedInput, seedOffset);

  usedColorsMap.set(cacheKey, selectedColor);
  saveStoredColorMap();
  return selectedColor;
}

/**
 * Generate color from string using HSL → Hex (algorithmic, no palette)
 */
export function stringToColor(str: string): string {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = str.charCodeAt(i) + ((hash << 5) - hash);
  }

  const hue = hash % 360;
  const saturation = 50 + (hash % 30); // 50%–80%
  const lightness = 35 + (hash % 25); // 35%–60%

  return hslToHex(hue, saturation, lightness);
}

/**
 * Convert HSL → Hex
 */
function hslToHex(h: number, s: number, l: number): string {
  s /= 100;
  l /= 100;

  const c = (1 - Math.abs(2 * l - 1)) * s;
  const x = c * (1 - Math.abs(((h / 60) % 2) - 1));
  const m = l - c / 2;

  let r = 0,
    g = 0,
    b = 0;

  if (h >= 0 && h < 60) {
    r = c;
    g = x;
    b = 0;
  } else if (h >= 60 && h < 120) {
    r = x;
    g = c;
    b = 0;
  } else if (h >= 120 && h < 180) {
    r = 0;
    g = c;
    b = x;
  } else if (h >= 180 && h < 240) {
    r = 0;
    g = x;
    b = c;
  } else if (h >= 240 && h < 300) {
    r = x;
    g = 0;
    b = c;
  } else if (h >= 300 && h < 360) {
    r = c;
    g = 0;
    b = x;
  }

  r = Math.round((r + m) * 255);
  g = Math.round((g + m) * 255);
  b = Math.round((b + m) * 255);

  // Convert to hex and pad with zeros if necessary
  const toHex = (n: number) => n.toString(16).padStart(2, "0");
  return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
}
